import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import api, { formatGBP, formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function Checkout() {
  const { items, subtotal } = useCart();
  const { user } = useAuth();
  const [shippingMethod, setShippingMethod] = useState("standard");
  const [loading, setLoading] = useState(false);
  const [promo, setPromo] = useState({ code: "", discount: 0, applied: null });
  const [redeemPoints, setRedeemPoints] = useState(0);
  const [me, setMe] = useState(user);
  const [form, setForm] = useState({
    email: user?.email || "", name: user?.name || "",
    line1: "", city: "", postcode: "", country: "United Kingdom",
  });
  const set = (k, v) => setForm({ ...form, [k]: v });

  useEffect(() => {
    if (user) api.get("/auth/me").then(({ data }) => setMe(data)).catch(() => {});
  }, [user]);

  if (items.length === 0) {
    return (
      <main className="max-w-3xl mx-auto px-6 py-32 text-center">
        <h1 className="font-serif text-3xl mb-4">Your bag is empty</h1>
        <Link to="/shop" className="btn-primary">Browse Collection</Link>
      </main>
    );
  }

  const availablePoints = me?.loyalty_points || 0;
  const loyaltyValue = redeemPoints / 100;
  const afterDiscounts = Math.max(0, subtotal - promo.discount - loyaltyValue);
  const shippingCost = afterDiscounts >= 100 ? 0 : (shippingMethod === "express" ? 8.99 : 3.99);
  const total = afterDiscounts + shippingCost;

  const applyPromo = async () => {
    if (!promo.code) return;
    try {
      const { data } = await api.post("/promo/validate", { code: promo.code, subtotal });
      setPromo({ ...promo, discount: data.discount, applied: data.code });
      toast.success(`Code ${data.code} applied — saved ${formatGBP(data.discount)}`);
    } catch (err) {
      setPromo({ ...promo, discount: 0, applied: null });
      toast.error(formatApiError(err));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/session", {
        items: items.map(({ id, quantity }) => ({ product_id: id, quantity })),
        origin_url: window.location.origin,
        shipping_method: shippingMethod,
        customer_email: form.email,
        promo_code: promo.applied || null,
        redeem_points: redeemPoints,
        shipping_address: {
          name: form.name, line1: form.line1, city: form.city,
          postcode: form.postcode, country: form.country,
        },
      });
      sessionStorage.setItem("aura_pending_session", data.session_id);
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err));
      setLoading(false);
    }
  };

  return (
    <main className="max-w-[1480px] mx-auto px-6 md:px-12 py-14" data-testid="checkout-page">
      <h1 className="font-serif text-4xl md:text-5xl mb-12">Checkout</h1>
      <div className="grid lg:grid-cols-3 gap-12">
        <form onSubmit={submit} className="lg:col-span-2 space-y-10" data-testid="checkout-form">
          <section>
            <p className="eyebrow text-neutral-600 mb-4">Contact</p>
            <input type="email" required placeholder="Email address" value={form.email} onChange={(e) => set("email", e.target.value)}
              data-testid="checkout-email" className="w-full bg-cream border border-hair px-4 py-3.5 text-sm outline-none focus:border-[var(--gold)]" />
          </section>
          <section>
            <p className="eyebrow text-neutral-600 mb-4">Shipping</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <input required placeholder="Full name" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="ship-name" className="bg-cream border border-hair px-4 py-3.5 text-sm outline-none focus:border-[var(--gold)] sm:col-span-2" />
              <input required placeholder="Address" value={form.line1} onChange={(e) => set("line1", e.target.value)} data-testid="ship-line1" className="bg-cream border border-hair px-4 py-3.5 text-sm outline-none focus:border-[var(--gold)] sm:col-span-2" />
              <input required placeholder="City" value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="ship-city" className="bg-cream border border-hair px-4 py-3.5 text-sm outline-none focus:border-[var(--gold)]" />
              <input required placeholder="Postcode" value={form.postcode} onChange={(e) => set("postcode", e.target.value)} data-testid="ship-postcode" className="bg-cream border border-hair px-4 py-3.5 text-sm outline-none focus:border-[var(--gold)]" />
            </div>
          </section>
          <section>
            <p className="eyebrow text-neutral-600 mb-4">Delivery method</p>
            <div className="space-y-3">
              {[
                { v: "standard", l: "Royal Mail Tracked 48", d: "2–3 working days", c: afterDiscounts >= 100 ? 0 : 3.99 },
                { v: "express", l: "Royal Mail Tracked 24", d: "Next working day", c: afterDiscounts >= 100 ? 0 : 8.99 },
              ].map((o) => (
                <label key={o.v} className={`flex items-center justify-between border px-4 py-4 cursor-pointer ${shippingMethod === o.v ? "border-neutral-900" : "border-hair"}`}>
                  <div className="flex items-center gap-4">
                    <input type="radio" name="ship" checked={shippingMethod === o.v} onChange={() => setShippingMethod(o.v)} data-testid={`ship-${o.v}`} />
                    <div><p className="text-sm font-medium">{o.l}</p><p className="text-xs text-neutral-500">{o.d}</p></div>
                  </div>
                  <span className="text-sm">{o.c === 0 ? "Free" : formatGBP(o.c)}</span>
                </label>
              ))}
            </div>
          </section>
          <button disabled={loading} type="submit" className="btn-primary w-full" data-testid="pay-stripe-btn">
            {loading ? "Redirecting…" : `Continue to Payment · ${formatGBP(total)}`}
          </button>
          <p className="text-xs text-neutral-500 text-center">You will be redirected to Stripe to complete your payment securely.</p>
        </form>

        <aside className="bg-cream border border-hair p-6 lg:p-8 self-start space-y-5" data-testid="checkout-summary">
          <div>
            <p className="eyebrow text-neutral-600 mb-4">Order Summary</p>
            <div className="space-y-3 max-h-64 overflow-auto pr-2 no-scrollbar">
              {items.map((it) => (
                <div key={it.id} className="flex gap-3" data-testid={`summary-${it.id}`}>
                  <div className="w-14 h-16 bg-white"><img src={it.image} alt="" className="w-full h-full object-cover" /></div>
                  <div className="flex-1 text-sm"><p className="font-serif">{it.name}</p><p className="text-xs text-neutral-500">Qty {it.quantity}</p></div>
                  <p className="text-sm">{formatGBP(it.price * it.quantity)}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-hair pt-5">
            <p className="eyebrow text-neutral-600 mb-3">Promo Code</p>
            <div className="flex gap-2">
              <input value={promo.code} onChange={(e) => setPromo({ ...promo, code: e.target.value.toUpperCase() })} placeholder="WELCOME10"
                data-testid="promo-input" className="flex-1 bg-white border border-hair px-3 py-2 text-sm uppercase outline-none focus:border-[var(--gold)]" />
              <button type="button" onClick={applyPromo} className="btn-ghost" data-testid="promo-apply">Apply</button>
            </div>
            {promo.applied && <p className="text-xs text-[var(--gold)] mt-2">✓ {promo.applied} — −{formatGBP(promo.discount)}</p>}
          </div>

          {user && availablePoints > 0 && (
            <div className="border-t border-hair pt-5" data-testid="loyalty-section">
              <p className="eyebrow text-neutral-600 mb-3">Loyalty Points</p>
              <p className="text-xs text-neutral-500 mb-3">{availablePoints} available · 100 pts = £1</p>
              <input type="range" min="0" max={Math.min(availablePoints, Math.floor(subtotal * 100))} step="100"
                value={redeemPoints} onChange={(e) => setRedeemPoints(parseInt(e.target.value))}
                data-testid="loyalty-slider" className="w-full accent-[var(--gold)]" />
              <p className="text-xs mt-1">Redeem {redeemPoints} pts → −{formatGBP(loyaltyValue)}</p>
            </div>
          )}

          <div className="border-t border-hair pt-5 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-neutral-600">Subtotal</span><span>{formatGBP(subtotal)}</span></div>
            {promo.discount > 0 && <div className="flex justify-between text-[var(--gold)]"><span>Promo</span><span>−{formatGBP(promo.discount)}</span></div>}
            {loyaltyValue > 0 && <div className="flex justify-between text-[var(--gold)]"><span>Points</span><span>−{formatGBP(loyaltyValue)}</span></div>}
            <div className="flex justify-between"><span className="text-neutral-600">Shipping</span><span>{shippingCost === 0 ? "Free" : formatGBP(shippingCost)}</span></div>
            <div className="flex justify-between font-serif text-lg pt-2 border-t border-hair mt-3"><span>Total</span><span data-testid="total-amount">{formatGBP(total)}</span></div>
          </div>
        </aside>
      </div>
    </main>
  );
}
